import React, { useEffect, useState } from "react";
import {
  Flex,
  Image,
  Text,
  Button,
  Box,
  useDisclosure,
  useMediaQuery,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AiOutlineMenu } from "react-icons/ai";
import { HiOutlineMoon, HiOutlineSun } from "react-icons/hi2";
import { motion, AnimatePresence } from "framer-motion";
import { headers } from "../components/common/constants";
import { IoIosArrowDown } from "react-icons/io";
import { scroller } from "react-scroll";
import Menu from "./Menu";

const Header = () => {
  const navigate = useNavigate();
  const [isMobile] = useMediaQuery("(max-width: 1350px)");
  const [hoverIndex, setHoverIndex] = useState("");
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [scroll, setScroll] = useState(false);
  const location = useLocation();
  const { colorMode, toggleColorMode } = useColorMode();

  const headerBg = useColorModeValue(
    scroll ? "rgba(255, 255, 255, 0.72)" : "rgba(255,255,255,0.6)",
    scroll ? "rgba(10,22,40,0.75)" : "rgba(10,22,40,0.6)"
  );
  const headerBorder = useColorModeValue(
    scroll ? "slate.200" : "transparent",
    scroll ? "rgba(255,255,255,0.08)" : "transparent"
  );
  const pillBg = useColorModeValue("rgba(248,250,252,0.55)", "rgba(255,255,255,0.05)");
  const pillBorder = useColorModeValue("slate.200", "rgba(255,255,255,0.08)");
  const navTextColor = useColorModeValue("slate.700", "slate.300");
  const navActiveBg = useColorModeValue("white", "rgba(255,255,255,0.08)");
  const navActiveText = useColorModeValue("brand.600", "accent.400");
  const menuBtnBg = useColorModeValue("slate.100", "rgba(255,255,255,0.08)");

  useEffect(() => {
    const handleScroll = () => setScroll(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (location?.state?.section) {
      scrollToSection(location.state.section);
    }
  }, [location]);

  const scrollToSection = (sectionId, retryCount = 5) => {
    const element = document.getElementById(sectionId);

    if (element) {
      scroller.scrollTo(sectionId, {
        duration: 800,
        delay: 0,
        smooth: "easeInOutQuart",
        offset: -150,
      });
    } else if (retryCount > 0) {
      setTimeout(() => scrollToSection(sectionId, retryCount - 1), 500);
    } else {
      console.warn(`Element with ID ${sectionId} not found.`);
    }
  };

  const handleNavigation = (dat, subItem) => {
    const targetPath = subItem.path.toLowerCase();

    if (dat.name === "About Us") {
      if (window.location.pathname !== "/about-us") {
        navigate("/about-us", { state: { section: targetPath } });
      } else {
        scrollToSection(targetPath);
      }
    } else if (dat.name === "Services") {
      if (window.location.pathname !== "/services") {
        navigate("/services", { state: { section: targetPath } });
      } else {
        scrollToSection(targetPath);
      }
    } else {
      navigate(targetPath);
    }

    setHoverIndex(null);
  };

  return (
    <Flex
      as={motion.header}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      bg={headerBg}
      backdropFilter="blur(18px) saturate(1.6)"
      borderBottom={scroll ? "1px solid" : "1px solid transparent"}
      borderColor={headerBorder}
      boxShadow={scroll ? "0 8px 30px -18px rgba(15,23,42,0.15)" : "none"}
      id="header"
      zIndex={666}
      w="full"
      pos="sticky"
      sx={{
        transition:
          "background .3s ease, box-shadow .3s ease, border-color .3s ease, height .3s ease",
      }}
      top="0"
      justifyContent="center"
      h={isMobile ? "70px" : scroll ? "72px" : "88px"}
      align="center"
    >
      <Box w={isMobile ? "100%" : "1350px"}>
        <Menu isOpen={isOpen} onClose={onClose} />
        <Flex px="20px" align="center" w="full" justifyContent="space-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            <Box w={{ base: "100%", md: "unset" }}>
              <Image
                onClick={() => navigate("/")}
                cursor="pointer"
                src="/assets/logo.png"
                alt="TransAlliance Capital"
                w={isMobile ? "150px" : "165px"}
                h={isMobile ? "28px" : "38px"}
                objectFit="contain"
                transition="transform .3s, filter .3s"
                filter={colorMode === "dark" ? "brightness(0) invert(1)" : "none"}
                _hover={{ transform: "scale(1.04)" }}
              />
            </Box>
          </motion.div>

          <Flex
            justifyContent="flex-end"
            display={isMobile ? "flex" : "none"}
            w="100%"
            align="center"
            gap="8px"
          >
            <Box
              as="button"
              aria-label="Toggle theme"
              onClick={toggleColorMode}
              p="10px"
              borderRadius="10px"
              bg={menuBtnBg}
              color={colorMode === "dark" ? "gold.400" : "slate.700"}
              transition="all .2s"
              _hover={{ transform: "translateY(-1px)" }}
            >
              {colorMode === "dark" ? (
                <HiOutlineSun size="20px" />
              ) : (
                <HiOutlineMoon size="20px" />
              )}
            </Box>
            <Box
              as="button"
              onClick={onOpen}
              p="10px"
              borderRadius="10px"
              bg={menuBtnBg}
              transition="all .2s"
            >
              <AiOutlineMenu size="22px" />
            </Box>
          </Flex>

          <Flex
            display={isMobile ? "none" : "flex"}
            gap="6px"
            align="center"
            bg={pillBg}
            border="1px solid"
            borderColor={pillBorder}
            px="10px"
            py="6px"
            borderRadius="999px"
          >
            {headers.map((dat, i) => {
              const isActive = location.pathname === dat?.path;
              return (
                <Box
                  key={i}
                  pos="relative"
                  cursor="pointer"
                  onMouseEnter={() => setHoverIndex(i)}
                  onMouseLeave={() => setHoverIndex("")}
                >
                  <AnimatePresence>
                    {dat?.sub && hoverIndex === i && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2 }}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          paddingTop: "14px",
                          zIndex: 99,
                        }}
                      >
                        <Box
                          bg="white"
                          w="220px"
                          borderRadius="16px"
                          p="8px"
                          border="1px solid"
                          borderColor="slate.200"
                          boxShadow="0 20px 40px -20px rgba(15,23,42,0.25)"
                        >
                          {dat.sub.map((subItem, subIndex) => (
                            <Box
                              key={subIndex}
                              onClick={() => handleNavigation(dat, subItem)}
                              px="14px"
                              py="10px"
                              cursor="pointer"
                              borderRadius="10px"
                              fontSize="15px"
                              fontWeight={500}
                              color="slate.700"
                              transition="all .2s"
                              _hover={{
                                bg: "brand.50",
                                color: "brand.600",
                                transform: "translateX(4px)",
                              }}
                            >
                              {subItem.name}
                            </Box>
                          ))}
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <NavLink to={dat?.path}>
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.05 + i * 0.06 }}
                    >
                      <Flex
                        align="center"
                        gap="6px"
                        px="16px"
                        py="8px"
                        borderRadius="999px"
                        pos="relative"
                        bg={isActive ? navActiveBg : "transparent"}
                        boxShadow={
                          isActive
                            ? "0 4px 14px -6px rgba(15,23,42,0.15)"
                            : "none"
                        }
                        transition="all .25s"
                        _hover={!isActive ? { bg: navActiveBg } : {}}
                      >
                        <Text
                          fontSize="14.5px"
                          fontWeight={isActive ? 700 : 500}
                          color={isActive ? navActiveText : navTextColor}
                        >
                          {dat.name}
                        </Text>
                        {dat.sub && (
                          <IoIosArrowDown
                            size="12px"
                            color={isActive ? "#2563EB" : "#64748B"}
                          />
                        )}
                      </Flex>
                    </motion.div>
                  </NavLink>
                </Box>
              );
            })}
          </Flex>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Flex align="center" gap="10px" display={isMobile ? "none" : "flex"}>
              <Box
                as="button"
                aria-label="Toggle theme"
                onClick={toggleColorMode}
                w="44px"
                h="46px"
                borderRadius="12px"
                bg={pillBg}
                border="1px solid"
                borderColor={pillBorder}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color={colorMode === "dark" ? "gold.400" : "slate.700"}
                transition="all .25s"
                _hover={{ transform: "translateY(-2px)" }}
              >
                {colorMode === "dark" ? (
                  <HiOutlineSun size="18px" />
                ) : (
                  <HiOutlineMoon size="18px" />
                )}
              </Box>
              <Button
                h="46px"
                px="26px"
                fontSize="15px"
                onClick={() => navigate("/contact-us")}
              >
                Get in touch
              </Button>
            </Flex>
          </motion.div>
        </Flex>
      </Box>
    </Flex>
  );
};

export default Header;
